import React, { useEffect, useState } from "react";
import { api, fileUrl } from "@/planner/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import PackList from "@/planner/components/PackList";
import { DollarSign, TrendingUp, Package, Boxes, Download, SlidersHorizontal, Printer } from "lucide-react";

const money = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);

// Columns available in the per-machine fill-list CSV export. `always` columns
// cannot be turned off so the export is never empty.
const FILL_COLUMNS = [
  { key: "slot", label: "Slot" },
  { key: "product", label: "Product", always: true },
  { key: "size", label: "Size" },
  { key: "capacity", label: "Capacity" },
  { key: "have", label: "Have" },
  { key: "fill", label: "Fill" },
  { key: "cost", label: "Cost" },
  { key: "vend", label: "Vend" },
  { key: "margin", label: "Margin %" },
  { key: "fill_cost", label: "Fill cost" },
];

const fillCell = (r, key) => ({
  slot: r.slot_id,
  product: `${r.product_name}${r.variant ? ` ${r.variant}` : ""}`,
  size: r.size || "",
  capacity: r.capacity,
  have: r.current,
  fill: r.units_to_fill,
  cost: r.cost == null ? "" : Number(r.cost).toFixed(2),
  vend: r.vend == null ? "" : Number(r.vend).toFixed(2),
  margin: r.margin_pct == null ? "" : r.margin_pct,
  fill_cost: r.fill_cost == null ? "" : Number(r.fill_cost).toFixed(2),
}[key]);

// Build a CSV string from an array of rows (each row is an array of cells) and
// trigger a browser download.
const csvCell = (v) => {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const downloadCSV = (filename, rows) => {
  const csv = rows.map((r) => r.map(csvCell).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
const stamp = () => new Date().toISOString().slice(0, 10);
const slugify = (s) => (s || "machine").replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase();

const Stat = ({ icon: Icon, label, value, tone = "primary", testid }) => (
  <Card className="p-4" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="mt-0.5 font-heading text-2xl font-bold">{value}</div>
      </div>
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tone === "good" ? "bg-green-100 text-green-700" : "bg-primary/10 text-primary"}`}><Icon className="h-5 w-5" /></div>
    </div>
  </Card>
);

export default function FillPlanner() {
  const [machines, setMachines] = useState([]);
  const [mid, setMid] = useState("");
  const [plan, setPlan] = useState(null);
  const [fleet, setFleet] = useState(null);
  const [exportCols, setExportCols] = useState(() => FILL_COLUMNS.map((c) => c.key));

  useEffect(() => {
    api.get("/machines").then((r) => { setMachines(r.data); if (r.data[0]) setMid(r.data[0].id); }).catch((e) => console.error("machines load failed", e));
    api.get("/fill-plan/fleet").then((r) => setFleet(r.data)).catch((e) => console.error("fleet load failed", e));
  }, []);

  useEffect(() => {
    if (!mid) return;
    api.get(`/machines/${mid}/fill-plan`).then((r) => setPlan(r.data)).catch((e) => console.error("plan load failed", e));
  }, [mid]);

  const toggleExportCol = (key) => setExportCols((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));

  const exportMachineCSV = () => {
    if (!plan || !plan.rows.length) return toast.error("Nothing to export yet");
    // Keep the fixed column order, filtered by the user's selection.
    const chosen = FILL_COLUMNS.filter((c) => c.always || exportCols.includes(c.key));
    const name = plan.machine?.name || "machine";
    const rows = [];
    rows.push([`Fill plan — ${name}${plan.machine?.location ? ` (${plan.machine.location})` : ""}`]);
    rows.push([`Generated`, stamp()]);
    rows.push([]);
    rows.push(chosen.map((c) => c.label));
    plan.rows.forEach((r) => rows.push(chosen.map((c) => fillCell(r, c.key))));
    rows.push([]);
    rows.push(["Cases needed"]);
    rows.push(["Product", "Units", "Case pack", "Cases needed"]);
    plan.cases.forEach((c) => rows.push([c.product_name, c.units, c.case_pack ?? "", c.cases_needed ?? ""]));
    rows.push([]);
    rows.push(["Totals"]);
    rows.push(["Cost to fill", "Top-up cost", "Revenue if sold out", "Potential profit"]);
    rows.push([plan.totals.fill_cost, plan.totals.topup_cost, plan.totals.fill_revenue, plan.totals.fill_profit]);
    downloadCSV(`fill-plan-${slugify(name)}-${stamp()}.csv`, rows);
    toast.success("CSV downloaded");
  };

  const exportFleetCSV = () => {
    if (!fleet || !fleet.machines.length) return toast.error("Nothing to export yet");
    const rows = [];
    rows.push(["Fleet fill plan"]);
    rows.push(["Generated", stamp()]);
    rows.push([]);
    rows.push(["Machine", "Location", "Slots filled", "Fill cost", "Top-up cost", "Profit"]);
    fleet.machines.forEach((m) => rows.push([m.name, m.location || "", m.slots_filled, m.fill_cost, m.topup_cost, m.fill_profit]));
    rows.push([]);
    rows.push(["Fleet totals"]);
    rows.push(["Fill cost", "Top-up cost", "Revenue if sold out", "Potential profit", "Machines"]);
    rows.push([fleet.totals.fill_cost, fleet.totals.topup_cost, fleet.totals.fill_revenue, fleet.totals.fill_profit, fleet.totals.machine_count]);
    rows.push([]);
    rows.push(["Total cases needed (whole fleet)"]);
    rows.push(["Product", "Units", "Case pack", "Cases needed"]);
    fleet.cases.forEach((c) => rows.push([c.product_name, c.units, c.case_pack ?? "", c.cases_needed ?? ""]));
    downloadCSV(`fleet-fill-plan-${stamp()}.csv`, rows);
    toast.success("CSV downloaded");
  };

  const printFleetSheet = () => {
    if (!fleet || !fleet.machines.length) return toast.error("Nothing to print yet");
    const esc = (v) => String(v ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    const caseRows = fleet.cases.map((c) => `<tr><td>${esc(c.product_name)}${c.size ? ` <span class="loc">${esc(c.size)}</span>` : ""}</td><td class="n">${c.units}</td><td class="n">${c.cases_needed == null ? "—" : c.cases_needed}</td><td class="n">${c.case_cost == null ? "—" : "$" + Number(c.case_cost).toFixed(2)}</td><td class="chk"></td></tr>`).join("");
    const totalCases = fleet.cases.reduce((s, c) => s + (c.cases_needed || 0), 0);
    const totalCaseCost = fleet.cases.reduce((s, c) => s + (c.case_cost || 0), 0);
    const machineRows = fleet.machines.map((m) => `<tr><td>${esc(m.name)}${m.location ? ` <span class="loc">(${esc(m.location)})</span>` : ""}</td><td class="n">${m.slots_filled}</td><td class="n">$${Number(m.fill_cost).toFixed(2)}</td><td class="n">$${Number(m.topup_cost).toFixed(2)}</td></tr>`).join("");
    const doc = `<!doctype html><html><head><title>Fleet restock pick sheet</title>
      <style>
        *{box-sizing:border-box} body{font-family:Arial,Helvetica,sans-serif;margin:24px;color:#111}
        h1{font-size:20px;margin:0 0 2px} h2{font-size:14px;margin:20px 0 6px}
        .sub{color:#555;font-size:12px;margin:0 0 8px}
        table{border-collapse:collapse;width:100%;margin-bottom:8px} td,th{border:1px solid #999;padding:6px;font-size:12px;text-align:left}
        th{background:#f2f2f2} td.n,th.n{text-align:right} td.chk{width:60px} .loc{color:#666;font-size:11px}
        tfoot td{font-weight:bold;background:#fafafa} .tot{font-size:12px;color:#333;margin-top:4px}
        @media print{body{margin:10mm}}
      </style></head><body>
      <h1>Fleet restock — pick sheet</h1>
      <p class="sub">Generated ${stamp()} · ${fleet.totals.machine_count} machines</p>
      <h2>Cases to pull</h2>
      <table><thead><tr><th>Product</th><th class="n">Units</th><th class="n">Cases</th><th class="n">Case cost</th><th>Loaded ✓</th></tr></thead>
        <tbody>${caseRows || '<tr><td colspan="5">No cases needed</td></tr>'}</tbody>
        <tfoot><tr><td>Total</td><td class="n"></td><td class="n">${totalCases}</td><td class="n">$${totalCaseCost.toFixed(2)}</td><td></td></tr></tfoot>
      </table>
      <h2>Machines</h2>
      <table><thead><tr><th>Machine</th><th class="n">Slots</th><th class="n">Fill cost</th><th class="n">Top-up</th></tr></thead><tbody>${machineRows}</tbody></table>
      <p class="tot"><b>Fleet totals</b> — Cases: ${totalCases} ($${totalCaseCost.toFixed(2)}) · Fill cost: $${Number(fleet.totals.fill_cost).toFixed(2)} · Top-up: $${Number(fleet.totals.topup_cost).toFixed(2)} · Revenue: $${Number(fleet.totals.fill_revenue).toFixed(2)} · Profit: $${Number(fleet.totals.fill_profit).toFixed(2)}</p>
      <script>window.onload=function(){setTimeout(function(){window.print();},250);};<\/script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to print"); return; }
    w.document.write(doc);
    w.document.close();
  };

  return (
    <div>
      <div className="mb-5">
        <h1 className="font-heading text-2xl font-bold">Fill Planner</h1>
        <p className="text-sm text-muted-foreground">Cost, margin, units and cases needed to fill a machine — built from your planogram capacities and product economics.</p>
      </div>

      <Tabs defaultValue="pack">
        <TabsList>
          <TabsTrigger value="pack" data-testid="tab-fill-pack">Packing list</TabsTrigger>
          <TabsTrigger value="machine" data-testid="tab-fill-machine">Per machine</TabsTrigger>
          <TabsTrigger value="fleet" data-testid="tab-fill-fleet">Whole fleet</TabsTrigger>
        </TabsList>

        <TabsContent value="pack" className="mt-4"><PackList /></TabsContent>

        <TabsContent value="machine" className="mt-4">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <div className="max-w-xs flex-1 min-w-[200px]">
              <Select value={mid} onValueChange={setMid}>
                <SelectTrigger data-testid="fill-machine-select"><SelectValue placeholder="Choose a machine" /></SelectTrigger>
                <SelectContent>{machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}{m.location ? ` · ${m.location}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" data-testid="fill-columns-btn"><SlidersHorizontal className="mr-2 h-4 w-4" />Columns</Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-56" data-testid="fill-columns-popover">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">CSV columns</div>
                <div className="space-y-2">
                  {FILL_COLUMNS.map((c) => (
                    <label key={c.key} className={`flex items-center gap-2 text-sm ${c.always ? "opacity-60" : "cursor-pointer"}`}>
                      <Checkbox
                        checked={c.always || exportCols.includes(c.key)}
                        disabled={c.always}
                        data-testid={`fill-col-${c.key}`}
                        onCheckedChange={() => !c.always && toggleExportCol(c.key)}
                      />
                      <span>{c.label}{c.always ? " (always)" : ""}</span>
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            <Button variant="outline" data-testid="fill-export-machine" onClick={exportMachineCSV} disabled={!plan || !plan.rows.length}><Download className="mr-2 h-4 w-4" />Export CSV</Button>
          </div>

          {!plan ? <Card className="p-8 text-center text-muted-foreground">Select a machine.</Card> : (
            <>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-4">
                <Stat icon={DollarSign} label="Cost to fill" value={money(plan.totals.fill_cost)} testid="fill-total-cost" />
                <Stat icon={Package} label="Top-up cost" value={money(plan.totals.topup_cost)} testid="fill-total-topup" />
                <Stat icon={TrendingUp} label="Revenue if sold out" value={money(plan.totals.fill_revenue)} tone="good" testid="fill-total-revenue" />
                <Stat icon={TrendingUp} label="Potential profit" value={money(plan.totals.fill_profit)} tone="good" testid="fill-total-profit" />
              </div>

              <Card className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="fill-table">
                  <thead className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="p-2.5">Slot</th><th className="p-2.5">Product</th>
                      <th className="p-2.5 text-right">Cap</th><th className="p-2.5 text-right">Have</th>
                      <th className="p-2.5 text-right">Fill</th><th className="p-2.5 text-right">Cost</th>
                      <th className="p-2.5 text-right">Vend</th><th className="p-2.5 text-right">Margin</th>
                      <th className="p-2.5 text-right">Fill cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.rows.length === 0 ? (
                      <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">No products assigned to this machine yet.</td></tr>
                    ) : plan.rows.map((r) => (
                      <tr key={r.slot_id} className="border-t" data-testid={`fill-row-${r.slot_id}`}>
                        <td className="p-2.5 font-mono">{r.slot_id}</td>
                        <td className="p-2.5">
                          <div className="flex items-center gap-2">
                            {r.image_path ? <img src={fileUrl(r.image_path)} alt="" className="h-8 w-8 rounded border object-cover" /> : null}
                            <span className="truncate">{r.product_name}{r.variant ? ` ${r.variant}` : ""}</span>
                            {r.size ? <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold">{r.size}</span> : null}
                          </div>
                        </td>
                        <td className="p-2.5 text-right">{r.capacity}</td>
                        <td className="p-2.5 text-right">{r.current}</td>
                        <td className="p-2.5 text-right font-semibold">{r.units_to_fill}</td>
                        <td className="p-2.5 text-right">{money(r.cost)}</td>
                        <td className="p-2.5 text-right">{money(r.vend)}</td>
                        <td className="p-2.5 text-right">{r.margin_pct == null ? "—" : <Badge variant="secondary" className={r.below_target ? "bg-destructive/15 text-destructive" : "bg-green-100 text-green-800"} data-testid={`fill-margin-${r.slot_id}`}>{r.margin_pct}%{r.below_target ? " ⚠" : ""}</Badge>}</td>
                        <td className="p-2.5 text-right font-mono">{money(r.fill_cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>

              {plan.cases.length > 0 && (
                <div className="mt-4">
                  <h3 className="mb-2 flex items-center gap-1.5 font-heading font-semibold"><Boxes className="h-4 w-4" />Cases needed</h3>
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="fill-cases">
                    {plan.cases.map((c) => (
                      <Card key={c.product_id} className="flex items-center justify-between p-3 text-sm" data-testid={`fill-case-${c.product_id}`}>
                        <span className="truncate">{c.product_name}</span>
                        <span className="text-right">{c.cases_needed == null ? <span className="text-xs text-muted-foreground">set case pack</span> : <><b>{c.cases_needed}</b> case{c.cases_needed !== 1 ? "s" : ""} <span className="text-xs text-muted-foreground">({c.units}÷{c.case_pack})</span></>}</span>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="fleet" className="mt-4">
          {!fleet ? <Card className="p-8 text-center text-muted-foreground">Loading…</Card> : (
            <>
              <div className="mb-4 flex justify-end gap-2">
                <Button variant="outline" data-testid="fleet-print-btn" onClick={printFleetSheet} disabled={!fleet.machines.length}><Printer className="mr-2 h-4 w-4" />Print pick sheet</Button>
                <Button variant="outline" data-testid="fill-export-fleet" onClick={exportFleetCSV} disabled={!fleet.machines.length}><Download className="mr-2 h-4 w-4" />Export CSV</Button>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-4">
                <Stat icon={DollarSign} label="Fleet fill cost" value={money(fleet.totals.fill_cost)} testid="fleet-total-cost" />
                <Stat icon={Package} label="Fleet top-up cost" value={money(fleet.totals.topup_cost)} testid="fleet-total-topup" />
                <Stat icon={TrendingUp} label="Revenue if sold out" value={money(fleet.totals.fill_revenue)} tone="good" testid="fleet-total-revenue" />
                <Stat icon={TrendingUp} label="Potential profit" value={money(fleet.totals.fill_profit)} tone="good" testid="fleet-total-profit" />
              </div>
              <Card className="overflow-x-auto mb-4">
                <table className="w-full text-sm" data-testid="fleet-table">
                  <thead className="bg-muted/60 text-left text-xs uppercase text-muted-foreground"><tr><th className="p-2.5">Machine</th><th className="p-2.5 text-right">Slots</th><th className="p-2.5 text-right">Fill cost</th><th className="p-2.5 text-right">Top-up</th><th className="p-2.5 text-right">Profit</th></tr></thead>
                  <tbody>
                    {fleet.machines.map((m) => (
                      <tr key={m.id} className="border-t" data-testid={`fleet-row-${m.id}`}>
                        <td className="p-2.5">{m.name}{m.location ? <span className="text-xs text-muted-foreground"> · {m.location}</span> : ""}</td>
                        <td className="p-2.5 text-right">{m.slots_filled}</td>
                        <td className="p-2.5 text-right font-mono">{money(m.fill_cost)}</td>
                        <td className="p-2.5 text-right font-mono">{money(m.topup_cost)}</td>
                        <td className="p-2.5 text-right font-mono text-green-700">{money(m.fill_profit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
              {fleet.cases.length > 0 && (
                <div>
                  <h3 className="mb-2 flex items-center gap-1.5 font-heading font-semibold"><Boxes className="h-4 w-4" />Total cases needed (whole fleet)</h3>
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="fleet-cases">
                    {fleet.cases.map((c) => (
                      <Card key={c.product_id} className="flex items-center justify-between p-3 text-sm">
                        <span className="truncate">{c.product_name}</span>
                        <span>{c.cases_needed == null ? <span className="text-xs text-muted-foreground">set case pack</span> : <><b>{c.cases_needed}</b> case{c.cases_needed !== 1 ? "s" : ""} <span className="text-xs text-muted-foreground">({c.units})</span></>}</span>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
